import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  PendingSessionData,
  SessionState,
} from 'features/session/interfaces';

export const initialSessionState: SessionState = {
  id: 0,
  rnsConnectionId: '',
  pendingSession: null,
  isRnsSessionStarted: false,
  numberOfConsecutiveAnswerTimeouts: 0,
  sessionRemovalStatus: 'idle',
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState: initialSessionState,
  reducers: {
    setRnsConnectionId: (state, action: PayloadAction<string>) => {
      state.rnsConnectionId = action.payload;
      state.sessionRemovalStatus = 'idle';
    },
    setSessionId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
      // A new session has its own removal to perform, so the guard against
      // removing the previous one twice has to be re-armed here.
      state.sessionRemovalStatus = 'idle';
    },
    resetSessionId: (state) => {
      state.id = initialSessionState.id;
      state.isRnsSessionStarted = initialSessionState.isRnsSessionStarted;
      state.numberOfConsecutiveAnswerTimeouts =
        initialSessionState.numberOfConsecutiveAnswerTimeouts;
    },
    setSessionPending: (state, action: PayloadAction<PendingSessionData>) => {
      state.pendingSession = action.payload;
    },
    clearSessionPending: (state) => {
      state.pendingSession = null;
    },
    rnsSessionStarted: (state) => {
      state.isRnsSessionStarted = true;
    },
    rnsSessionEnded: (state) => {
      state.isRnsSessionStarted = false;
    },
    answerTimeoutOccurred: (state) => {
      state.numberOfConsecutiveAnswerTimeouts += 1;
    },
    resetAnswerTimeoutCount: (state) => {
      state.numberOfConsecutiveAnswerTimeouts = 0;
    },
    sessionRemovalStarted: (state) => {
      state.sessionRemovalStatus = 'removing';
    },
    sessionRemovalCompleted: (state) => {
      state.sessionRemovalStatus = 'removed';
    },
    sessionRemovalFailed: (state) => {
      state.sessionRemovalStatus = 'idle';
    },
  },
});

export const {
  setRnsConnectionId,
  setSessionId,
  resetSessionId,
  setSessionPending,
  clearSessionPending,
  rnsSessionStarted,
  rnsSessionEnded,
  answerTimeoutOccurred,
  resetAnswerTimeoutCount,
  sessionRemovalStarted,
  sessionRemovalCompleted,
  sessionRemovalFailed,
} = sessionSlice.actions;

export const sessionReducer = sessionSlice.reducer;
